import { create } from 'zustand'

import { ApiError } from '@/api/errors'

/**
 * The global `PLAN_LIMIT_REACHED` handler (launch plan §5): any mutation the backend refuses
 * because the plan is full opens one upgrade prompt, wherever the user was. The details are the
 * backend's own (`PlanLimitReachedError`): which resource, the limit, the plan, and a message
 * written for the user. No React here, so the query client can call it from its MutationCache.
 */
export interface PlanLimitDetails {
  readonly resource: string
  readonly limit: number | null
  readonly plan: string
  readonly message: string
}

interface UpgradePromptState {
  readonly details: PlanLimitDetails | null
  show: (details: PlanLimitDetails) => void
  dismiss: () => void
}

export const useUpgradePromptStore = create<UpgradePromptState>()((set) => ({
  details: null,
  show: (details) => {
    set({ details })
  },
  dismiss: () => {
    set({ details: null })
  },
}))

export function planLimitFromError(error: unknown): PlanLimitDetails | null {
  if (!(error instanceof ApiError) || error.code !== 'PLAN_LIMIT_REACHED') return null
  const d = error.details
  const resource = typeof d['resource'] === 'string' ? d['resource'] : 'items'
  const limit = typeof d['limit'] === 'number' ? d['limit'] : null
  const plan = typeof d['plan'] === 'string' ? d['plan'] : 'current'
  const message =
    typeof d['upgrade_message'] === 'string' && d['upgrade_message'].trim() !== ''
      ? d['upgrade_message']
      : error.message
  return { resource, limit, plan, message }
}

/** Wired into the query client's MutationCache; exported so tests can call it directly. */
export function reportMutationError(error: unknown): void {
  const details = planLimitFromError(error)
  if (details !== null) useUpgradePromptStore.getState().show(details)
}
